import { Box, Button, Typography } from '@mui/material';
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import { ESortDirection, ETableStructureType, TTableStructure } from 'ui/EasTable/types';
import { EasTableWithFilter } from 'ui/EasTableWithFilter';
import { DeleteDialog } from 'ui/DeleteDialog';
import { PAGE_SIZE } from 'services/constants/env';
import { TFilters, TOrderBy } from 'redux/types';
import { getFiltersFromNavigateParams } from 'utils/FilterParams';
import {
	deleteMonthlyFeeRequest,
	getFeeClassOptionsRequest,
	getMonthlyFeeListRequest,
} from 'redux/reducers/easFee/reducer';
import {
	getFeeClassOptions,
	getMonthlyFeeDetailsList,
	getMonthlyFeeDetailsListLoading,
} from 'redux/reducers/easFee/selectors';
import { TMonthlyFeeDetails } from 'redux/reducers/easFee/types';
import { getEmEnumsRequest } from 'redux/reducers/emManagement/reducer';
import { EEmEnums } from 'redux/reducers/emManagement/constants';
import { getEnumsSupportedCurrency } from 'redux/reducers/emManagement/selectors';
import { FEE_CLASS_SELECTOR, tableStructure as defaultTableStructure } from './tableStructure';
import { MonthlyFeeDialog } from './MonthlyFeeDialog';
import { resolveCurrencyName } from '../utils';

const EDIT_ACTION = 'Edit';
const DELETE_ACTION = 'Delete';

export const MonthlyFees: FC = () => {
	const dispatch = useDispatch();
	const { search } = useLocation();

	const [currentTableStructure, setCurrentTableStructure] = useState(defaultTableStructure);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [monthlyFeeToEdit, setMonthlyFeeToEdit] = useState<TMonthlyFeeDetails | null>(null);
	const [idToDelete, setIdToDelete] = useState<number | null>(null);

	const list = useSelector(getMonthlyFeeDetailsList);
	const listLoading = useSelector(getMonthlyFeeDetailsListLoading);
	const supportedCurrency = useSelector(getEnumsSupportedCurrency);
	const feeClassOptions = useSelector(getFeeClassOptions);

	useEffect(() => {
		dispatch(getEmEnumsRequest({ enumName: EEmEnums.SUPPORTED_CURRENCY }));
		dispatch(getFeeClassOptionsRequest());
	}, [dispatch]);

	const structure = useMemo(
		() =>
			currentTableStructure.map((column) => {
				if (column.type === ETableStructureType.AMOUNT) {
					return {
						...column,
						currency: (item: TMonthlyFeeDetails) =>
							resolveCurrencyName(supportedCurrency, item.currency),
					};
				}
				if (column.selector === FEE_CLASS_SELECTOR) {
					return {
						...column,
						value: (item: TMonthlyFeeDetails) =>
							feeClassOptions?.find((option) => option.id === item.feeClassId)?.description ?? '',
					};
				}
				return column;
			}),
		[currentTableStructure, feeClassOptions, supportedCurrency],
	);

	/** Keeps only the visibility flags, so the columns stay free of the derived render callbacks. */
	const handleTableStructureChange = useCallback(
		(newTableStructure: TTableStructure<keyof TMonthlyFeeDetails>[]) => {
			setCurrentTableStructure((previous) =>
				previous.map((column) => {
					const changed = newTableStructure.find((item) => item.selector === column.selector);
					return changed ? { ...column, show: changed.show } : column;
				}),
			);
		},
		[],
	);

	const loadList = useCallback(() => {
		const params = queryString.parse(search);
		const filters = getFiltersFromNavigateParams(currentTableStructure, params) as TFilters;

		const orderBy: TOrderBy[] = [];
		if (params?.sortIndex) {
			orderBy.push({
				fieldName: defaultTableStructure[+params.sortIndex].selector as string,
				direction:
					String(params.sortDirection) === 'desc' ? ESortDirection.DESC : ESortDirection.ASC,
			});
		}
		orderBy.push({ fieldName: 'id', direction: ESortDirection.DESC });

		dispatch(
			getMonthlyFeeListRequest({
				pageNumber: params.page ? +params.page : 1,
				pageSize: PAGE_SIZE,
				searchString: String(params?.search ?? ''),
				filters,
				orderBy,
			}),
		);
	}, [currentTableStructure, dispatch, search]);

	useEffect(() => {
		loadList();
	}, [loadList]);

	const handleAddNew = useCallback(() => {
		setMonthlyFeeToEdit(null);
		setDialogOpen(true);
	}, []);

	const handleAction = useCallback(
		(id: number, action: string) => {
			if (action === EDIT_ACTION) {
				setMonthlyFeeToEdit(list?.data?.find((item) => item.id === id) ?? null);
				setDialogOpen(true);
			}
			if (action === DELETE_ACTION) {
				setIdToDelete(id);
			}
		},
		[list],
	);

	const handleDialogClose = useCallback(() => {
		setDialogOpen(false);
		setMonthlyFeeToEdit(null);
	}, []);

	const handleDeleteClose = useCallback(() => {
		setIdToDelete(null);
	}, []);

	const handleDeleteConfirm = useCallback(() => {
		if (idToDelete !== null) {
			dispatch(deleteMonthlyFeeRequest({ id: idToDelete }));
		}
		setIdToDelete(null);
	}, [dispatch, idToDelete]);

	return (
		<>
			<MonthlyFeeDialog
				open={dialogOpen}
				onClose={handleDialogClose}
				monthlyFeeToEdit={monthlyFeeToEdit}
			/>
			<DeleteDialog
				open={idToDelete !== null}
				setClose={handleDeleteClose}
				handleDelete={handleDeleteConfirm}
				text="Are you sure you want to delete this monthly fee?"
			/>
			<EasTableWithFilter<TMonthlyFeeDetails>
				tableStructure={structure}
				items={list}
				itemsIsLoading={listLoading}
				handleAction={handleAction}
				onTableStructureChange={handleTableStructureChange}
			>
				<Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
					<Typography variant="body1" color="primary">
						Resources / Reference Tables / Fees / <b>Monthly Subscriptions</b>
					</Typography>
					<Button variant="contained" color="primary" size="small" onClick={handleAddNew}>
						Add New Monthly Subscription
					</Button>
				</Box>
			</EasTableWithFilter>
		</>
	);
};
